<template>
  <v-navigation-drawer
    v-model="drawer.active"
    width="400px"
    :height="drawer.mode == 'search' ? '80vh' : '55vh'"
    disable-route-watcher
    hide-overlay
  >
    <!-- Search mode -->
    <div
      v-if="drawer.mode == 'search'"
      class="sidebar-drawer-header sidebar-drawer-header-search"
    >
      <div class="sidebar-drawer-header-input">
        <!-- No Input -->
        <div class="no-search-result pb-4">
          <h4>{{ $t("sidebar-menu.sidebar-noinput-title") }}</h4>
        </div>

        <BaseInput
          :value.sync="search"
          :placeholder="$t('sidebar-menu.sidebar-noinput-message')"
          outlined
          dense
          icon="mdi-magnify"
        />
      </div>
      <div class="sidebar-drawer-header-button">
        <div @click="handleDrawerClose('search')">
          <BaseIcon size="18px" icon="close" hover />
        </div>
      </div>
    </div>

    <!-- Theme mode -->
    <div
      v-else-if="drawer.mode == 'themes'"
      class="sidebar-drawer-header"
      :class="getAppName()"
    >
      <div class="sidebar-drawer-header-title p-4 pl-6">
        <h4>{{ $t("sidebar-menu.sidebar-theme-select") }}</h4>
      </div>
      <div class="sidebar-drawer-header-button">
        <div @click="handleDrawerClose('themes')">
          <BaseIcon size="18px" icon="close" hover />
        </div>
      </div>
    </div>

    <!-- Route mode -->
    <div v-else-if="drawer.mode == 'routes'" class="sidebar-drawer-header">
      <div class="sidebar-drawer-header-title p-4 pl-6">
        <h4>{{ $t("sidebar-menu.sidebar-route-select") }}</h4>
      </div>
      <div class="sidebar-drawer-header-button">
        <div @click="handleDrawerClose('routes')">
          <BaseIcon size="18px" icon="close" hover />
        </div>
      </div>
    </div>

    <!-- 5D search -->
    <div
      v-if="drawer.mode == 'search'"
      class="sidebar-drawer-list sidebar-drawer-list-search"
      :class="getAppName()"
      @scroll="fadeGradientOnScroll"
    >
      <div
        class="listview-gradient"
        :class="{
          'listview-gradient-huygens': getAppName() == 'huygens',
          'listview-gradient-dordrecht5D': getAppName() == 'dordrecht5D',
          'listview-gradient-canals5D': getAppName() == 'canals5D'
        }"
      />
      <v-list>
        <v-list-item
          v-for="item in get5DSearch()"
          :key="`search_${item.reference_id}_${item.title}`"
          v-ripple="false"
          :class="`sidebar-drawer-list-item-search ${getAppName()}`"
          @click.prevent="select5DItem(item)"
        >
          <div
            class="image-container"
            :style="{
              'background-image': 'url(' + `${get5DImage(item.media)}` + ')'
            }"
          ></div>
          <div class="sidebar-drawer-list-item-title">
            <h4 class="ml-2">{{ item.title }}</h4>
          </div>
        </v-list-item>
      </v-list>

      <!-- No search results -->
      <div
        v-if="search.length > 2 && !search5D.length"
        class="no-search-result"
      >
        <h4>{{ $t("sidebar-menu.sidebar-noresults-title") }}</h4>
      </div>
    </div>

    <!-- Theme list -->
    <div
      v-else-if="drawer.mode == 'themes'"
      :class="getAppName()"
      class="sidebar-drawer-list"
      @scroll="fadeGradientOnScroll"
    >
      <div
        class="listview-gradient"
        :class="{
          'listview-gradient-huygens': getAppName() == 'huygens',
          'listview-gradient-dordrecht5D': getAppName() == 'dordrecht5D',
          'listview-gradient-canals5D': getAppName() == 'canals5D'
        }"
      />
      <v-list>
        <v-list-item
          v-for="theme in getThemes()"
          :key="`theme_${theme.id}_${theme.title}`"
          v-ripple="false"
          class="sidebar-drawer-list-item"
          @click.prevent="selectTheme(theme)"
        >
          <div
            class="sidebar-drawer-list-item-image"
            :class="{
              'sidebar-drawer-list-item-image-huygens':
                getAppName() == 'huygens',
              'sidebar-drawer-list-item-image-dordrecht5D':
                getAppName() == 'dordrecht5D',
              'sidebar-drawer-list-item-image-canals5D':
                getAppName() == 'canals5D'
            }"
          >
            <img :src="`data:image/png;base64, ${theme.icon}`" />
          </div>
          <span>
            <h5 class="sidebar-drawer-list-item-title">
              {{ theme.title }}
            </h5>
          </span>
        </v-list-item>
      </v-list>
    </div>

    <!-- Route list -->
    <div v-else class="sidebar-drawer-list" @scroll="fadeGradientOnScroll">
      <div
        class="listview-gradient"
        :class="{
          'listview-gradient-huygens': getAppName() == 'huygens',
          'listview-gradient-dordrecht5D': getAppName() == 'dordrecht5D',
          'listview-gradient-canals5D': getAppName() == 'canals5D'
        }"
      />
      <v-list>
        <v-list-item
          v-for="route in getRoutes()"
          :key="`route_${route.id}_${route.title}`"
          v-ripple="false"
          class="sidebar-drawer-list-item"
          @click.prevent="selectRoute(route)"
        >
          <div
            class="sidebar-drawer-list-item-image"
            :class="{
              'sidebar-drawer-list-item-image-huygens':
                getAppName() == 'huygens',
              'sidebar-drawer-list-item-image-dordrecht5D':
                getAppName() == 'dordrecht5D',
              'sidebar-drawer-list-item-image-canals5D':
                getAppName() == 'canals5D'
            }"
          >
            <img :src="`data:image/png;base64, ${route.icon}`" />
          </div>
          <span>
            <h5 class="sidebar-drawer-list-item-title">
              {{ route.title }}
            </h5>
          </span>
        </v-list-item>
      </v-list>
    </div>
  </v-navigation-drawer>
</template>

<script>
import { mapState } from "vuex";
import { appIdentifier } from "@/utils/appIdentifier.js";

export default {
  name: "SidebarDrawer",
  props: {
    themes: {
      type: Array,
      default: () => []
    },
    routes: {
      type: Array,
      default: () => []
    }
  },
  data: function() {
    return {
      search: ""
    };
  },
  watch: {
    themes() {
      this.getThemes();
    },
    routes() {
      this.getRoutes();
    },
    search5D() {
      this.get5DSearch();
    },
    locale() {
      //reset search on locale change
      this.search = "";
      this.$store.commit(this.$mutations.REMOVE_5D_SEARCH);
    },
    search: function(term) {
      if (term == "") {
        //Clear on empty
        this.$store.commit(this.$mutations.REMOVE_5D_SEARCH);
      }

      if (term.length >= 3)
        this.$store.dispatch(this.$actions.GET_5D_SEARCH, term);
    }
  },
  computed: {
    ...mapState({
      locale: state => state.locale,
      drawer: state => state.sidebarDrawer,
      search5D: state => state.search5D
    }),
    get5DItems() {
      return [{ id: 1, title: "title", image: null }];
    }
  },
  methods: {
    getAppName() {
      return appIdentifier();
    },
    getThemes() {
      return this.themes;
    },
    getRoutes() {
      return this.routes;
    },
    get5DSearch() {
      return this.search5D;
    },
    get5DImage(media) {
      if (media.length) {
        return media[0].image_url;
      } else {
        return `${require(`@/assets/images/${this.getAppName()}/search-hotspot.png`)}`;
      }
    },
    handleDrawerClose(mode) {
      this.$store.commit(this.$mutations.TOGGLE_SIDEBAR_MENU, {
        mode: mode
      });
    },
    fadeGradientOnScroll(event) {
      const gradient = event.target.querySelector(
        `.listview-gradient-${this.getAppName()}`
      );

      if (event.target.scrollTop > 0) {
        gradient.classList.add("show");
      } else {
        gradient.classList.remove("show");
      }
    },
    selectTheme(theme) {
      this.$store.dispatch(this.$actions.SELECT_THEME, theme);
      this.$store.commit(this.$mutations.TOGGLE_SIDEBAR_MENU, {
        mode: "themes"
      });
    },
    selectRoute(route) {
      this.$store.dispatch(this.$actions.SELECT_ROUTE, route);
      this.$store.commit(this.$mutations.TOGGLE_SIDEBAR_MENU, {
        mode: "routes"
      });
    },
    select5DItem(item) {
      if (item.type == "hotspot") {
        this.$router.push({
          name: "panorama-view",
          params: {
            id: item.reference_id
          }
        });
      }

      if (item.type == "article") {
        this.$store.commit(this.$mutations.TOGGLE_MENU, {
          name: "articleDrawer"
        });
        this.$store.dispatch(this.$actions.GET_ARTICLE, item.reference_id);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.v-navigation-drawer {
  z-index: 2;
  background-color: var(--v-brand-base) !important;
  opacity: 0.9;
  direction: rtl;
  border-top-right-radius: 25px !important;
  position: absolute !important;
  top: auto !important;
  bottom: 0 !important;
  z-index: 10;
  overflow-y: hidden !important;

  .v-navigation-drawer__content {
    .v-list {
      display: block;
      padding: 0 50px 20px 25px;

      &-item {
        padding: 5px;
      }
    }
  }
}

.listview-gradient {
  opacity: 0;
  transition: opacity 0.1s ease-in-out;
  pointer-events: none;
  position: absolute;
  top: 20;
  width: 100%;
  height: 30px;
  z-index: 10;

  &-huygens {
    background: linear-gradient(
      180deg,
      rgba($brand-huygens, 1),
      rgba($brand-huygens, 0)
    );
  }

  &-dordrecht5D {
    background: linear-gradient(
      180deg,
      rgba($brand-dordrecht5D, 1),
      rgba($brand-dordrecht5D, 0)
    );
  }

  &-canals5D {
    background: linear-gradient(
      180deg,
      rgba($brand-canals5D, 1),
      rgba($brand-canals5D, 0)
    );
  }

  &.show {
    opacity: 1;
  }
}

.sidebar-drawer {
  position: absolute !important;
  left: 0 !important;
  bottom: 0 !important;

  border-top-right-radius: 25px !important;

  &-list {
    width: 100%;
    height: calc(100% - 70px);
    direction: ltr;
    overflow-y: auto;
  }

  &-list-search {
    height: calc(100% - 170px);
  }

  &-list-item {
    &-title {
      max-width: 225px;
      padding: 5px;
      overflow-wrap: break-word;

      h4 {
        font-size: 1.5rem;
      }
    }

    &-image {
      height: 44px;
      width: 44px;
      background-color: #ffffff;
      border-radius: 22px;
      padding: 8px;
      border: 1px solid var(--v-secondary-base);

      img {
        object-fit: contain;
        height: 100%;
        width: 100%;
      }

      &-canals5D {
        border: 1px solid var(--v-primary-base);
        background-color: #efe8d7;
      }

      &-huygens {
        border: 1px solid #bebb9b;
      }
    }

    &-search {
      border-bottom: 1px solid #ffffff66;

      &.canals5D {
        border-bottom: 1px solid rgba($secondary-canals5D, 0.4);
      }

      div.image-container {
        display: inline-block;
        width: 58px;
        height: 58px;
        border-radius: 29px;
        margin: 10px 10px 10px 0;
        background-size: cover;
        background-position: center;
      }
    }

    span {
      padding-left: 18px;
    }
  }

  &-header {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    direction: ltr;
    margin: 5px 0;
    height: 60px;

    &-title {
      justify-content: center;
      text-align: center;
    }

    &-button {
      position: absolute;
      top: 20px;
      right: 20px;

      &:hover {
        cursor: pointer;
      }
    }

    &-input::v-deep {
      width: 290px;
      margin-top: 20px;
      padding: 0px 0 20p 0;

      .v-icon {
        color: white !important;
      }
    }
  }

  &-header-search {
    height: 130px;
  }
}

.no-search-result {
  text-align: center;
  margin: 0 20px;
}
</style>
